import React, { useState, useEffect } from "react";
import SEO from "../seo";
const BuyXGetY = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "how-to-access",
        "set-up-your-campaign",
        "preview-and-publish",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Buy X Get Y | Boost Sales with Smart Promotions – Xpage Help Center"
        description="Learn how to set up and manage Buy X Get Y promotions in Xpage. Encourage more purchases by offering discounts or free items with flexible conditions."
        keywords="Buy X Get Y, promotion, discount, offers, bundles, marketing, Xpage Help Center"
        canonical="https://help.xpage.ai/help/business-expansion/Buy%20X%20Get%20Y"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Buy X Get Y
          </h1>
          <p className="text-sm text-systemLabel">
            The Buy X Get Y promotion motivates customers to purchase more by
            offering free or discounted products once a certain condition is
            met. This strategy boosts transaction value and encourages repeat
            sales, making it ideal for a wide range of scenarios.
          </p>

          <h3 className="text-sm font-semibold text-systemTitle mb-2 mt-4">
            Examples:
          </h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
            <li>Buy one ice cream cone, get the second at half price</li>
            <li>Get 10% off accessories with an iPhone 16 purchase</li>
          </ul>
        </header>

        {/* How to Access Section */}
        <section id="how-to-access" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to Access
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">Log in to your Xpage Admin.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the left sidebar, go to Offers → Create Bundle.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Select Buy X Get Y.</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Set Up Your Campaign Section */}
        <section id="set-up-your-campaign" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Set Up Your Campaign
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Setting
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Discount Method
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Automatic discount: Applied automatically on the product
                    page, cart, and checkout.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Discount Title
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Give your promotion a clear, descriptive name (e.g., Summer
                    BOGO Special).
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Customer Purchase
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Purchase requirements: Define a minimum purchase, either by:
                    <br />
                    • Quantity (e.g., Buy 2)
                    <br />• Apply to specific products
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Customer Get
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        Quantity: Set how many discounted items the customer
                        will receive.
                      </li>
                      <li>
                        Eligible products: Select the product(s) or
                        collection(s) the discount applies to.
                      </li>
                      <li>
                        Discount amount: Choose percentage off, fixed amount
                        off, or free.
                      </li>
                      <li>
                        Apply to higher/lower priced items: Decide whether
                        discounts apply to the most or least expensive items
                        when limits are exceeded.
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Include multiple
                items to diversify your discount offers.
              </p>
            </div>
          </div>
        </section>

        {/* Preview and Publish Section */}
        <section id="preview-and-publish" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Preview and Publish
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Review all rule settings in the Overview section.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Preview (top-right) to check your configuration before
                  going live.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  When ready, click Save to activate the offer.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Once saved, the promotion will appear in your Offers list,
                  where you can edit, activate, deactivate, or delete it
                  anytime.
                </span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                With Buy X Get Y, you can create flexible promotions that reward
                customers while driving higher sales volume.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#how-to-access"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "how-to-access"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                How to Access
              </a>
            </li>
            <li>
              <a
                href="#set-up-your-campaign"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "set-up-your-campaign"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Set Up Your Campaign
              </a>
            </li>
            <li>
              <a
                href="#preview-and-publish"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "preview-and-publish"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Preview and Publish
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BuyXGetY;
